import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, AlertCircle, CheckCircle2, MapPin, Phone, Mail, Briefcase, Sparkles } from 'lucide-react';
import { appName } from '../config/branding';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClaimPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading, login } = useAuth();

  const [proData, setProData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [errorCode, setErrorCode] = useState(null);
  const [claiming, setClaiming] = useState(false);

  // Step 1: fetch the imported pro's preview data
  useEffect(() => {
    if (!token) return;
    const fetchClaim = async () => {
      try {
        const res = await axios.get(`${API}/auth/claim/${token}`);
        setProData(res.data);
      } catch (e) {
        setErrorCode(e.response?.status || 500);
        setError(e.response?.data?.detail || 'Invitation could not be loaded.');
      } finally {
        setLoading(false);
      }
    };
    fetchClaim();
  }, [token]);

  // Step 2: if user signs in mid-flow, try to complete the claim automatically
  useEffect(() => {
    if (!user || !proData || claiming) return;
    if ((user.email || '').toLowerCase() !== (proData.email || '').toLowerCase()) return;
    const complete = async () => {
      setClaiming(true);
      try {
        const res = await axios.post(`${API}/auth/claim/${token}/complete`, {}, { withCredentials: true });
        navigate(res.data.redirect_to || `/dashboard/${res.data.professional_id}/leads`);
      } catch (e) {
        setErrorCode(e.response?.status || 500);
        setError(e.response?.data?.detail || 'Could not complete activation.');
      } finally {
        setClaiming(false);
      }
    };
    complete();
  }, [user, proData, token, navigate, claiming]);

  // --- Render states ---

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 size={28} className="animate-spin text-gray-400" />
      </div>
    );
  }

  if (errorCode) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white border border-red-200 rounded-2xl p-8 text-center">
          <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
            <AlertCircle size={22} className="text-red-600" />
          </div>
          <h1 className="text-lg font-bold text-gray-900 mb-2">
            {errorCode === 404 && 'Invitation not found'}
            {errorCode === 409 && 'Already claimed'}
            {errorCode === 410 && 'Invitation expired'}
            {errorCode === 403 && 'Wrong email'}
            {![404, 409, 410, 403].includes(errorCode) && 'Could not activate'}
          </h1>
          <p className="text-sm text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/login')}
            data-testid="claim-go-to-login"
            className="text-sm font-semibold text-[#37b34a] hover:underline"
          >
            Go to sign in
          </button>
        </div>
      </div>
    );
  }

  if (!proData) return null;

  const userEmailMatches =
    user && (user.email || '').toLowerCase() === (proData.email || '').toLowerCase();

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-10">
      <div className="max-w-lg mx-auto">
        <div className="bg-white border border-gray-200 rounded-2xl p-8" data-testid="claim-card">
          <div className="flex items-center gap-2 text-[#37b34a] text-xs font-bold uppercase tracking-wide mb-3">
            <Sparkles size={14} /> Founding member invitation
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-1">
            Welcome, {proData.business_name}
          </h1>
          <p className="text-sm text-gray-500 mb-6">
            Verify the details below, then sign in with Google to activate your {appName} account.
          </p>

          <div className="space-y-3 mb-6 text-sm">
            <div className="flex items-start gap-2 text-gray-700">
              <Mail size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-gray-900">{proData.email}</p>
                <p className="text-xs text-gray-500">You'll need to sign in with this Google account.</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <Phone size={16} className="text-gray-400 flex-shrink-0" />
              <span>{proData.phone || 'Phone not on file'}</span>
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <Briefcase size={16} className="text-gray-400 flex-shrink-0" />
              <span>{proData.services?.map(s => s.service_id).join(', ')}</span>
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <MapPin size={16} className="text-gray-400 flex-shrink-0" />
              <span>{proData.service_areas || `${proData.city || ''}, ${proData.province || ''}`}</span>
            </div>
            {proData.business_description && (
              <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-600 italic">
                "{proData.business_description}"
              </div>
            )}
          </div>

          {!user ? (
            <button
              onClick={login}
              data-testid="claim-sign-in-btn"
              className="w-full bg-[#37b34a] hover:bg-[#2d9a3e] text-white font-semibold py-3 rounded-full transition-colors"
            >
              Sign in with Google to activate
            </button>
          ) : userEmailMatches ? (
            <div className="flex items-center justify-center gap-2 bg-green-50 text-green-700 text-sm py-3 px-4 rounded-lg">
              {claiming ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Activating your account…
                </>
              ) : (
                <>
                  <CheckCircle2 size={14} /> Signed in — taking you to your dashboard
                </>
              )}
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              <p className="font-semibold mb-1">Wrong account.</p>
              <p className="text-xs">
                You're signed in as <strong>{user.email}</strong>, but this invite was sent to{' '}
                <strong>{proData.email}</strong>. Please sign out and sign in again with the correct Google account.
              </p>
            </div>
          )}

          <p className="mt-4 text-xs text-gray-400 text-center">
            Invitation valid for 24 hours from when it was sent.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ClaimPage;
